import type { SingleValue } from '@ksyos/design-system';
import type { JSX } from 'react';
import {
    Alert,
    Button,
    Col,
    FormGroup,
    FormSection,
    NumberInput,
    Row,
    Spacer,
    Switch,
    Text,
} from '@ksyos/design-system';
import { useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import type { ServiceOrganizationRole } from '@common/interfaces/api/servicesApi';
import type { IOption } from '@frontend/components/interfaces/option';
import type { IServiceOrganizationSettingRoleDistribution } from '@frontend/components/interfaces/serviceOrganizationSettingRoleDistribution';
import { formatString } from '@common/utils/formatString';
import { getSelectedLanguage } from '@frontend/translate';
import type { ServiceOrganizationRoleDistributionsErrors } from './serviceOrganizationRoleDistributionTypes';
import { ServiceOrganizationRoleDistributionRoleSelect } from './ServiceOrganizationRoleDistributionRoleSelect';
import { roleDistributionErrorTypes } from './serviceOrganizationRoleDistributionTypes';

interface IServiceOrganizationRoleDistributionEditProps {
    errors: ServiceOrganizationRoleDistributionsErrors;
    handleAddOrganization: (serviceOrganizationRole: ServiceOrganizationRole) => void;
    handleChangeOrganization: (previousOrganizationId: string, selectedOption: SingleValue<IOption<string>>) => void;
    handleDeleteOrganization: (organizationId: string) => void;
    handleDistributionChange: (percentage: number | null, id: string) => void;
    handleEqualDistributionChange: () => void;
    isEquallyDistributed: boolean;
    label: string;
    serviceOrganizationRoleDistributions: IServiceOrganizationSettingRoleDistribution[];
    serviceOrganizationRoles: ServiceOrganizationRole[];
    setIsEquallyDistributed: (value: boolean) => void;
    showValidationAlerts: boolean;
}

export function ServiceOrganizationRoleDistributionEdit(
    props: IServiceOrganizationRoleDistributionEditProps
): JSX.Element {
    const [showEmptySelect, setShowEmptySelect] = useState(false);

    const serviceOrganizationRolesToSelect = useMemo(
        () =>
            props.serviceOrganizationRoles.filter(
                (serviceOrganizationRole) =>
                    !props.serviceOrganizationRoleDistributions.some(
                        (serviceOrganizationRoleDistribution) =>
                            serviceOrganizationRoleDistribution.serviceOrganizationRole.id ===
                            serviceOrganizationRole.id
                    )
            ),
        [props.serviceOrganizationRoles, props.serviceOrganizationRoleDistributions]
    );

    return (
        <FormSection
            title={`${getSelectedLanguage().serviceOrganizationSettingRoleDistributions.partnerOrganizations} ${
                props.label
            }`}
        >
            {/* Spacing is required to style the add organization button in-between the form groups */}
            <Spacer>
                {props.serviceOrganizationRoleDistributions.map((serviceOrganizationRoleDistribution) => (
                    <ServiceOrganizationRoleDistributionRoleSelect
                        handleChangeOrganization={(value) => {
                            props.handleChangeOrganization(
                                serviceOrganizationRoleDistribution.serviceOrganizationRole.serviceOrganizationSetting
                                    .organization.id,
                                value
                            );
                            props.setIsEquallyDistributed(false);
                        }}
                        handleDeleteOrganization={() => {
                            props.handleDeleteOrganization(
                                serviceOrganizationRoleDistribution.serviceOrganizationRole.serviceOrganizationSetting
                                    .organization.id
                            );
                            props.setIsEquallyDistributed(false);
                        }}
                        key={uuidv4()}
                        options={getOptions()}
                        selectedOption={{
                            label: serviceOrganizationRoleDistribution.serviceOrganizationRole
                                .serviceOrganizationSetting.organization.name,
                            value: serviceOrganizationRoleDistribution.serviceOrganizationRole.id,
                        }}
                    />
                ))}

                {showEmptySelect && (
                    <ServiceOrganizationRoleDistributionRoleSelect
                        handleChangeOrganization={(value) => {
                            const serviceOrganizationRole = serviceOrganizationRolesToSelect.find(
                                (organizationToSelect) => organizationToSelect.id === value?.value
                            );
                            if (serviceOrganizationRole) {
                                props.handleAddOrganization(serviceOrganizationRole);
                                props.setIsEquallyDistributed(false);
                            }
                            setShowEmptySelect(false);
                        }}
                        handleDeleteOrganization={() => setShowEmptySelect(false)}
                        options={getOptions()}
                        selectedOption={null}
                    />
                )}

                {serviceOrganizationRolesToSelect.length > 0 && (
                    <Button
                        iconLeft="add"
                        onClick={() => setShowEmptySelect(true)}
                        variant="link"
                    >
                        {formatString(
                            getSelectedLanguage().serviceOrganizationSettingRoleDistributions.addOrganization,
                            [props.label]
                        )}
                    </Button>
                )}

                {props.serviceOrganizationRoleDistributions.length > 0 && (
                    <FormGroup
                        label={
                            getSelectedLanguage().serviceOrganizationSettingRoleDistributions
                                .distributeInEqualPercentages
                        }
                    >
                        <Switch
                            checked={props.isEquallyDistributed}
                            onChange={() => props.handleEqualDistributionChange()}
                        />
                    </FormGroup>
                )}

                {props.serviceOrganizationRoleDistributions.map((serviceOrganizationRoleDistribution) => (
                    <FormGroup
                        isRequired={true}
                        key={
                            serviceOrganizationRoleDistribution.serviceOrganizationRole.serviceOrganizationSetting
                                .organization.id
                        }
                        label={
                            serviceOrganizationRoleDistribution.serviceOrganizationRole.serviceOrganizationSetting
                                .organization.name
                        }
                    >
                        <Row
                            align="center"
                            columnGap={2}
                        >
                            <Col span={2}>
                                <NumberInput
                                    aria-label="Verdeling"
                                    isDisabled={props.isEquallyDistributed}
                                    maximumFractionDigits={props.isEquallyDistributed ? 2 : 0}
                                    onChange={(percentage) =>
                                        props.handleDistributionChange(
                                            percentage,
                                            serviceOrganizationRoleDistribution.serviceOrganizationRole.id
                                        )
                                    }
                                    value={serviceOrganizationRoleDistribution.percentage}
                                />
                            </Col>
                            <Col>
                                <Text>%</Text>
                            </Col>
                        </Row>
                    </FormGroup>
                ))}
            </Spacer>
            {props.showValidationAlerts &&
                roleDistributionErrorTypes
                    .filter((errorType) => props.errors.has(errorType))
                    .map((errorType) => (
                        <Alert
                            appearance="warning"
                            hasIcon={true}
                            key={errorType}
                        >
                            {props.errors.get(errorType)}
                        </Alert>
                    ))}
        </FormSection>
    );

    function getOptions(): IOption<string>[] {
        return serviceOrganizationRolesToSelect.map((serviceOrganizationRole) => ({
            label: serviceOrganizationRole.serviceOrganizationSetting.organization.name,
            value: serviceOrganizationRole.id,
        }));
    }
}
